/**
 * Excel 模板 → 模板骨架 JSON（纯逻辑，无 IO）。
 *
 * 定位：Excel 模板仅提供版式（几何/样式/文本），数据一律来自服务端现有数据源体系。
 * 本模块只做「无损提取 + 数据位启发式标记」，字段绑定的确认由 LLM 语义对齐 + 用户确认闭环完成。
 *
 * 输出骨架: sheet / rows(行高,引擎单位) / columns(列宽,引擎单位) / merges /
 *           cells(含 rowspan/colspan 与 style) / dataRegions
 *
 * 用法（已内置 CLI）:
 *   baskreport template extract --in 模板.xlsx --out out_template.json
 *   [--sheet <名>]   指定工作表（缺省取第一个）
 *   [--max-rows 100]  只输出前 N 行（大模板控制 token）
 *   [--max-cols 30]  只输出前 N 列
 *   [--max-text 30]  单元格文本截断长度
 *
 * 样式解析说明（标准 xlsx）:
 *   SheetJS 社区版 cellStyles:true 时 cell.s 只含 fill 对象（font/alignment/border 丢失），
 *   因此标准文件的完整样式由 xlsx.ts 自行解析 xl/styles.xml + theme1.xml，并提取
 *   sheet XML 中 <c s="N"> 的原始样式索引（styleIndex）传到此模块，经 CellXf 关联到
 *   Fonts/Fills/Borders/alignment。无 styleIndex 时回退 cell.s 内联形态（SheetJS 写入的文件）。
 */
import { createRequire } from 'node:module';
import type * as XLSX from 'xlsx';

import { DEFAULTS } from '../grid/defaults.js';
import type { TemplateBlock } from './regions.js';
import { segmentBlocks } from './regions.js';
import { detectDataSlots } from './detectSlots.js';

/** 懒加载 xlsx（模块缓存保证只解析一次）。 */
const require = createRequire(import.meta.url);
function getXlsx(): typeof XLSX {
  return require('xlsx') as typeof XLSX;
}

export interface SkeletonCell {
  row: number;
  col: number;
  text?: string;
  style?: Record<string, unknown>;
  /** 合并跨度（与 IR CellIR 对齐）；缺省 1，>1 表示合并 */
  rowspan?: number;
  colspan?: number;
  /** 启发式角色：label（标签）/ data（疑似数据位）/ static（静态文本） */
  candidate?: string;
  reason?: string;
}

/* ------------------------------------------------------------------ */
/* 自建样式表类型（xlsx.ts 解析 xl/styles.xml 后产生；统一为小写字段）  */
/* ------------------------------------------------------------------ */

export interface ResolvedColor {
  rgb?: string;
  theme?: number;
  tint?: number;
  indexed?: number;
}

export interface ResolvedFont {
  name?: string;
  sz?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: ResolvedColor;
}

export interface ResolvedFill {
  patternType?: string;
  fgColor?: ResolvedColor;
  bgColor?: ResolvedColor;
}

export interface ResolvedBorderPart {
  style?: string;
  color?: ResolvedColor;
}

export interface ResolvedBorder {
  left?: ResolvedBorderPart;
  right?: ResolvedBorderPart;
  top?: ResolvedBorderPart;
  bottom?: ResolvedBorderPart;
}

export interface ResolvedAlignment {
  horizontal?: string;
  vertical?: string;
  wrapText?: boolean;
}

export interface ResolvedXf {
  fontId?: number;
  fillId?: number;
  borderId?: number;
  numFmtId?: number;
  alignment?: ResolvedAlignment;
}

/** 完整样式表（与 SheetJS wb.Styles 字段对齐，但 Borders 含四边）。 */
export interface ResolvedStyles {
  fonts: ResolvedFont[];
  fills: ResolvedFill[];
  borders: ResolvedBorder[];
  cellXfs: ResolvedXf[];
}

/** SheetJS 社区版样式（内联形态 cell.s，或 wb.Styles 中颜色对象） */
interface SXColor {
  rgb?: string;
  theme?: number;
  indexed?: number;
}

/** 边框子集（对齐 IR CellBorder：style/width/color，缺省细黑实线）。 */
export interface StyleBorder {
  left?: { style: string; width?: number; color?: string };
  right?: { style: string; width?: number; color?: string };
  top?: { style: string; width?: number; color?: string };
  bottom?: { style: string; width?: number; color?: string };
}

/** Excel ARGB（8 位 hex，如 "FFFFFF00"）或 6 位 hex → IR 颜色 #RRGGBB；无/非法/全透明返回 undefined */
function argbToHex(argb?: string): string | undefined {
  if (!argb || typeof argb !== 'string') return undefined;
  const hex = argb.length === 8 ? argb.slice(2) : argb;
  if (hex.length === 3) return undefined; // SheetJS 对 indexed 色的解析失败产物（如 "000"），忽略
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  if (hex.slice(0, 2).toLowerCase() === '00' && argb.length === 8) return undefined; // 全透明
  return `#${hex}`;
}

/** 标准 Excel indexed 调色板（56 色，OLE 标准顺序）。 */
const INDEXED_COLORS: string[] = [
  '000000', 'FFFFFF', 'FF0000', '00FF00', '0000FF', 'FFFF00', 'FF00FF', '00FFFF',
  '000000', 'FFFFFF', 'FF0000', '00FF00', '0000FF', 'FFFF00', 'FF00FF', '00FFFF',
  '800000', '008000', '000080', '808000', '800080', '008080', 'C0C0C0', '808080',
  '9999FF', '993366', 'FFFFCC', 'CCFFFF', '660066', 'FF8080', '0066CC', 'CCCCFF',
  '000080', 'FF00FF', 'FFFF00', '00FFFF', '800080', '800000', '008080', '0000FF',
  '00CCFF', 'CCFFFF', 'CCFFCC', 'FFFF99', '99CCFF', 'FF99CC', 'CC99FF', 'FFCC99',
  '3366FF', '33CCCC', '99CC00', 'FFCC00', 'FF9900', 'FF6600', '666699', '969696',
  '003366', '339966', '003300', '333300', '993300', '993366', '333399', '333333',
];

/** Excel tint 亮度调整（-1..1；0 不变），入参/出参均为 6 位 hex。 */
function tintHex(rgb: string, tint: number): string {
  if (!tint) return rgb;
  const lum = (c: number) => (tint > 0 ? c * (1 - tint) + 255 * tint : c * (1 + tint));
  const h = (x: number) => Math.min(255, Math.max(0, Math.round(x))).toString(16).padStart(2, '0');
  return h(lum(parseInt(rgb.slice(0, 2), 16))) + h(lum(parseInt(rgb.slice(2, 4), 16))) + h(lum(parseInt(rgb.slice(4, 6), 16)));
}

/** 解析颜色 → #RRGGBB。优先级：显式 rgb > theme（themeClrs+tint）> indexed 调色板。 */
function resolveColorHex(color: ResolvedColor | SXColor | undefined, themeClrs?: Array<string | undefined>): string | undefined {
  if (!color) return undefined;
  const c = color as ResolvedColor;
  if (c.rgb) {
    const h = argbToHex(c.rgb);
    if (h) return h;
  }
  if (c.theme != null && themeClrs && themeClrs[c.theme]) {
    const base = themeClrs[c.theme]!;
    return c.tint ? `#${tintHex(base, c.tint)}` : `#${base}`;
  }
  if (c.indexed != null && INDEXED_COLORS[c.indexed]) return `#${INDEXED_COLORS[c.indexed]}`;
  return undefined;
}

/** 水平对齐归一化到 IR 枚举；fill/justify/distributed 等不可直接映射 → undefined（省略） */
function normalizeAlign(h?: string): 'left' | 'center' | 'right' | undefined {
  switch (h) {
    case 'left':
    case 'general':
      return 'left';
    case 'center':
    case 'centerContinuous':
      return 'center';
    case 'right':
      return 'right';
    default:
      return undefined;
  }
}

/** 垂直对齐归一化：Excel 的 center 即 IR 的 middle */
function normalizeValign(v?: string): 'top' | 'middle' | 'bottom' | undefined {
  switch (v) {
    case 'top':
      return 'top';
    case 'center':
      return 'middle';
    case 'bottom':
      return 'bottom';
    default:
      return undefined;
  }
}

/** 边框线型：thin/medium/thick/hair→solid；dashed/dashDot→dashed；dotted→dotted；其余不可映射 */
function normalizeBorderStyle(s?: string): 'solid' | 'dashed' | 'dotted' | undefined {
  switch (s) {
    case 'thin':
    case 'medium':
    case 'thick':
    case 'hair':
      return 'solid';
    case 'dashed':
    case 'dashDot':
      return 'dashed';
    case 'dotted':
      return 'dotted';
    default:
      return undefined;
  }
}

/** 边框线宽：thin/hair 即 DEFAULTS 宽 1（不输出）；medium→2、thick→3 */
function borderWidth(style?: string): number | undefined {
  switch (style) {
    case 'medium':
      return 2;
    case 'thick':
      return 3;
    default:
      return undefined;
  }
}

/** 由自建 ResolvedBorder 组装 IR 边框（黑=默认色剪枝）。 */
function pickBorderFromResolved(border: ResolvedBorder | undefined, themeClrs?: Array<string | undefined>): StyleBorder | undefined {
  if (!border) return undefined;
  const out: StyleBorder = {};
  for (const side of ['left', 'right', 'top', 'bottom'] as const) {
    const b = border[side];
    if (!b?.style) continue;
    const style = normalizeBorderStyle(b.style);
    if (!style) continue;
    const part: { style: string; width?: number; color?: string } = { style };
    const w = borderWidth(b.style);
    if (w) part.width = w;
    const color = resolveColorHex(b.color, themeClrs);
    if (color && color !== '#000000') part.color = color;
    out[side] = part;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** SheetJS 内联形态（cell.s 平铺 fill 对象，如 {patternType, fgColor, font?, alignment?, border?}）。 */
function pickStyleInline(cell: XLSX.CellObject, themeClrs?: Array<string | undefined>): Record<string, unknown> | undefined {
  if (cell.s == null || typeof cell.s === 'number') return undefined;
  const s = cell.s as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  const fore = resolveColorHex(s.font as ResolvedColor | undefined, themeClrs);
  if (fore && fore !== DEFAULTS.forecolor) out.forecolor = fore;
  const fill = s as { patternType?: string; fgColor?: ResolvedColor };
  const bg = fill?.patternType === 'solid' ? resolveColorHex(fill.fgColor, themeClrs) : undefined;
  if (bg && bg !== DEFAULTS.bgColor) out.bgcolor = bg;
  const align = normalizeAlign((s.alignment as { horizontal?: string } | undefined)?.horizontal);
  if (align && align !== DEFAULTS.align) out.align = align;
  const valign = normalizeValign((s.alignment as { vertical?: string } | undefined)?.vertical);
  if (valign && valign !== DEFAULTS.valign) out.valign = valign;
  const font = s.font as ResolvedFont | undefined;
  if (font?.name && font.name !== DEFAULTS.fontFamily) out.fontFamily = font.name;
  if (typeof font?.sz === 'number' && font.sz > 0 && font.sz <= 200 && font.sz !== DEFAULTS.fontSize) out.fontSize = font.sz;
  if (font?.bold) out.bold = true;
  if (font?.italic) out.italic = true;
  if (font?.underline) out.underline = true;
  const border = pickBorderFromResolved(s.border as ResolvedBorder | undefined, themeClrs);
  if (border) out.border = border;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * 由自建 ResolvedStyles + CellXf 索引组装 IR 样式。
 * 与引擎 DEFAULTS 相等的维度剪枝（序列化端同样"仅显式设置才输出"，避免骨架 token 膨胀）。
 */
function pickStyleFromXf(xf: ResolvedXf, styles: ResolvedStyles, themeClrs?: Array<string | undefined>): Record<string, unknown> | undefined {
  const font = xf.fontId != null ? styles.fonts[xf.fontId] : undefined;
  const fill = xf.fillId != null ? styles.fills[xf.fillId] : undefined;
  const border = xf.borderId != null ? styles.borders[xf.borderId] : undefined;
  const al = xf.alignment;
  const out: Record<string, unknown> = {};

  // 文字/背景颜色（背景仅 solid 填充可见）
  const fore = resolveColorHex(font?.color, themeClrs);
  if (fore && fore !== DEFAULTS.forecolor) out.forecolor = fore;
  const bg = fill?.patternType === 'solid' ? resolveColorHex(fill.fgColor, themeClrs) : undefined;
  if (bg && bg !== DEFAULTS.bgColor) out.bgcolor = bg;

  // 对齐
  const align = normalizeAlign(al?.horizontal);
  if (align && align !== DEFAULTS.align) out.align = align;
  const valign = normalizeValign(al?.vertical);
  if (valign && valign !== DEFAULTS.valign) out.valign = valign;

  // 字体
  if (font?.name && font.name !== DEFAULTS.fontFamily) out.fontFamily = font.name;
  if (typeof font?.sz === 'number' && font.sz > 0 && font.sz <= 200 && font.sz !== DEFAULTS.fontSize) out.fontSize = font.sz;
  if (font?.bold) out.bold = true;
  if (font?.italic) out.italic = true;
  if (font?.underline) out.underline = true;

  // 边框
  const b = pickBorderFromResolved(border, themeClrs);
  if (b) out.border = b;

  return Object.keys(out).length > 0 ? out : undefined;
}

/** 将 SheetJS wb.Styles（Fonts/Fills/Borders/CellXf 大写字段）归一化为 ResolvedStyles。 */
function normalizeSheetJsStyles(wbStyles: Record<string, unknown>): ResolvedStyles {
  const toColor = (c?: SXColor): ResolvedColor | undefined => {
    if (!c) return undefined;
    const o: ResolvedColor = {};
    if (c.rgb) o.rgb = c.rgb;
    if (c.theme != null) o.theme = c.theme;
    if (c.indexed != null) o.indexed = c.indexed;
    return o;
  };
  const fonts: ResolvedFont[] = ((wbStyles.Fonts as Array<Record<string, unknown>>) ?? []).map((f) => ({
    name: typeof f.name === 'string' ? f.name : undefined,
    sz: typeof f.sz === 'number' ? f.sz : undefined,
    bold: !!f.bold,
    italic: !!f.italic,
    underline: !!f.underline,
    color: toColor(f.color as SXColor),
  }));
  const fills: ResolvedFill[] = ((wbStyles.Fills as Array<Record<string, unknown>>) ?? []).map((f) => ({
    patternType: typeof f.patternType === 'string' ? f.patternType : undefined,
    fgColor: toColor(f.fgColor as SXColor),
    bgColor: toColor(f.bgColor as SXColor),
  }));
  // SheetJS 社区版 Borders 只解析 diagonal（四边全空），此处保留结构、样式缺失
  const borders: ResolvedBorder[] = ((wbStyles.Borders as Array<Record<string, unknown>>) ?? []).map(() => ({}));
  const cellXfs: ResolvedXf[] = ((wbStyles.CellXf as Array<Record<string, unknown>>) ?? []).map((x) => ({
    fontId: typeof x.fontId === 'number' ? x.fontId : undefined,
    fillId: typeof x.fillId === 'number' ? x.fillId : undefined,
    borderId: typeof x.borderId === 'number' ? x.borderId : undefined,
    numFmtId: typeof x.numFmtId === 'number' ? x.numFmtId : undefined,
    alignment: x.alignment as ResolvedAlignment | undefined,
  }));
  return { fonts, fills, borders, cellXfs };
}

/**
 * 标签启发：以冒号结尾（"发票号："）或 ≤6 字符的短文本（"发票号"）。
 * 调用方需额外排除「合并主格（标题）」与「表头行」，避免标题/表头被误判为标签。
 */
function isLabelText(t: string): boolean {
  const s = t.trim();
  return /[:：]$/.test(s) || (s.length > 0 && s.length <= 6 && !/^[\d\s.,-]+$/.test(s));
}

function markData(cells: SkeletonCell[], row: number, col: number, reason: string): void {
  const hit = cells.find((c) => c.row === row && c.col === col);
  if (hit) {
    hit.candidate = 'data';
    hit.reason = reason;
  } else {
    // 数据位候选是"空格"（无文本/无样式），未在 cells 中 → 新建一条空数据位记录
    cells.push({ row, col, candidate: 'data', reason });
  }
}

export interface TemplateSkeleton {
  sheet: { name: string; rows: number; cols: number };
  /** 行高（BaskReport 引擎单位，可直接搬入 IR rows[].height）。用户实测：引擎渲染 1 单位≈0.75px@96dpi，
   * 故 Excel hpt（点）→ 引擎单位 = round(hpt*4/3/0.75) = round(hpt*16/9)；缺省 27（Excel 默认 15pt） */
  rows: Array<{ num: number; height: number }>;
  /** 列宽（BaskReport 引擎单位，可直接搬入 IR columns[].width）。用户实测：引擎渲染 1 单位≈0.25px@96dpi，
   * 故 Excel wch（字符，px≈wch*7+5）→ 引擎单位 = round((wch*7+5)*4)；缺省 256（Excel 默认 8.43 字符） */
  columns: Array<{ num: number; width: number }>;
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>;
  cells: SkeletonCell[];
  dataRegions: Array<Record<string, unknown>>;
  /** 区块识别结果（可选，供知识库打分使用） */
  blocks?: TemplateBlock[];
}

export interface ExtractOptions {
  sheet?: string;
  maxRows: number;
  maxCols: number;
  maxText: number;
  /** 自建完整样式表（xlsx.ts 解析 styles.xml 后传入）；缺省回退 wb.Styles/内联形态 */
  styles?: ResolvedStyles;
  /** theme1.xml 的 clrScheme 色值数组（12 项，缺项 undefined） */
  themeClrs?: Array<string | undefined>;
  /** 单元格地址(A1) → 原始样式索引 s（sheet XML 提取） */
  styleIndex?: Map<string, number>;
}

/** Excel 默认行高 15pt 与默认列宽 8.43 字符（模板未显式设置行列尺寸时回退） */
const EXCEL_DEFAULT_ROW_HPT = 15;
const EXCEL_DEFAULT_COL_WCH = 8.43;

function hptToUnit(hpt: number): number {
  // 用户实测换算：BaskReport 行高渲染 ≈ 值×0.75px，Excel 行高需 hpt×4/3px，故输出 = hpt×4/3÷0.75 = hpt×16/9
  return Math.round((hpt * 4) / 3 / 0.75);
}

function wchToUnit(wch: number): number {
  // Excel 列宽需 (wch×7+5)px
  return Math.round(wch * 7 + 5);
}
/** 将单元格地址（行/列，1 基）转为 A1 风格，供工作表索引（如 A1 表示第 1 行第 1 列）。 */
function cellAddress(row: number, col: number): string {
  let s = '';
  let n = col;
  while (n > 0) {
    s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s + row;
}

/**
 * 提取模板骨架（纯函数，无 IO；便于用内存构造的 workbook 做确定性验证）。
 *
 * 样式解析优先级：
 *   1) opts.styleIndex + opts.styles（标准 xlsx：自建完整样式表，含边框/theme 色）——最完整
 *   2) opts.styleIndex + wb.Styles（SheetJS 解析的样式表，边框缺失）
 *   3) cell.s 内联形态（SheetJS 写入或内联样式的文件）
 */
export function extractTemplate(wb: XLSX.WorkBook, opts: ExtractOptions): TemplateSkeleton {
  const utils = getXlsx().utils;
  const sheetName = opts.sheet && wb.SheetNames.includes(opts.sheet) ? opts.sheet : wb.SheetNames[0];
  if (!sheetName) throw new Error('工作簿中没有任何工作表');
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error(`未找到工作表: ${sheetName}`);

  const range = utils.decode_range(ws['!ref'] ?? 'A1');
  const maxRow = Math.min(range.e.r + 1, opts.maxRows);
  const maxCol = Math.min(range.e.c + 1, opts.maxCols);

  // 合并区域：主格进 merges（+rowspan/colspan 映射供内联），被覆盖格记录后不单独输出
  const merges: Array<{ row: number; col: number; rowspan: number; colspan: number }> = [];
  const mergeSpans = new Map<string, { rowspan: number; colspan: number }>();
  const covered = new Set<string>();
  for (const m of ws['!merges'] ?? []) {
    if (m.s.r + 1 > maxRow || m.s.c + 1 > maxCol) continue;
    const rowspan = m.e.r - m.s.r + 1;
    const colspan = m.e.c - m.s.c + 1;
    merges.push({ row: m.s.r + 1, col: m.s.c + 1, rowspan, colspan });
    mergeSpans.set(`${m.s.r + 1},${m.s.c + 1}`, { rowspan, colspan });
    for (let r = m.s.r; r <= m.e.r; r++) {
      for (let c = m.s.c; c <= m.e.c; c++) {
        if (r !== m.s.r || c !== m.s.c) covered.add(`${r + 1},${c + 1}`);
      }
    }
  }

  // 行高列宽：显式设置优先，缺省回退 Excel 默认值
  const rows: Array<{ num: number; height: number }> = [];
  const wsRows = ws['!rows'] ?? [];
  for (let r = 1; r <= maxRow; r++) {
    rows.push({ num: r, height: hptToUnit(wsRows[r - 1]?.hpt ?? EXCEL_DEFAULT_ROW_HPT) });
  }
  const columns: Array<{ num: number; width: number }> = [];
  const wsCols = ws['!cols'] ?? [];
  for (let c = 1; c <= maxCol; c++) {
    columns.push({ num: c, width: wchToUnit(wsCols[c - 1]?.wch ?? EXCEL_DEFAULT_COL_WCH) });
  }

  // 样式上下文：自建完整样式表优先，回退 wb.Styles
  let resolved: ResolvedStyles | undefined = opts.styles;
  let themeClrs = opts.themeClrs;
  if (!resolved) {
    const wbStyles = (wb as unknown as { Styles?: Record<string, unknown> }).Styles;
    if (wbStyles?.CellXf) {
      resolved = normalizeSheetJsStyles(wbStyles);
    }
  }

  const styleOf = (r: number, c: number, cell: XLSX.CellObject | undefined): Record<string, unknown> | undefined => {
    if (!cell) return undefined;
    if (resolved && opts.styleIndex) {
      const sIdx = opts.styleIndex.get(cellAddress(r, c));
      if (sIdx != null) {
        const xf = resolved.cellXfs[sIdx];
        if (xf) return pickStyleFromXf(xf, resolved, themeClrs);
      }
    }
    return pickStyleInline(cell, themeClrs);
  };

  // 第一遍：填充文本网格 + 构建基础格列表（有文本/样式/合并跨度的格才输出）
  const cells: SkeletonCell[] = [];
  const textGrid: string[][] = Array.from({ length: maxRow + 1 }, () => Array<string>(maxCol + 1).fill(''));
  for (let r = 1; r <= maxRow; r++) {
    for (let c = 1; c <= maxCol; c++) {
      if (covered.has(`${r},${c}`)) continue;
      const cell = ws[cellAddress(r, c)];
      const text = cell ? String(utils.format_cell(cell)).trim().slice(0, opts.maxText) : '';
      textGrid[r]![c] = text;
      const style = styleOf(r, c, cell);
      const span = mergeSpans.get(`${r},${c}`);
      if (!text && !style && !span) continue; // 完全空白且无样式、非合并主格 → 省略（控 token）
      const rec: SkeletonCell = { row: r, col: c };
      if (text) rec.text = text;
      if (style) rec.style = style;
      if (span) {
        rec.rowspan = span.rowspan;
        rec.colspan = span.colspan;
      }
      cells.push(rec);
    }
  }

  // 表格数据区：某行连续 ≥2 个文本列（表头特征），且下一行同列全空 → 明细扩展区
  const headerRows = new Set<number>();
  const dataRegions: Array<Record<string, unknown>> = [];
  for (let r = 1; r <= maxRow - 1; r++) {
    const headerCols: number[] = [];
    for (let c = 1; c <= maxCol; c++) if (textGrid[r]![c]) headerCols.push(c);
    if (headerCols.length < 2) continue;
    if (!headerCols.every((c) => !textGrid[r + 1]![c])) continue;
    let rowCount = 1;
    while (r + rowCount <= maxRow && headerCols.every((c) => !textGrid[r + rowCount]![c])) rowCount++;
    headerRows.add(r);
    const headerCol0 = headerCols[0]!;
    dataRegions.push({
      kind: 'table',
      headerRow: r,
      headerCol: headerCol0,
      rowCount,
      headers: headerCols.map((c) => ({ row: r, col: c, text: textGrid[r]![c] })),
    });
    for (let i = 1; i < rowCount && i < 3; i++) {
      headerCols.forEach((c) => markData(cells, r + i, c, `表头'${textGrid[r]![c]}'下方数据区`));
    }
  }

  // 第二遍：标签标记（排除表头行与合并主格标题）→ 标签相邻空格标记
  const labels: Array<{ row: number; col: number; text: string }> = [];
  for (const rec of cells) {
    if (!rec.text || rec.candidate) continue;
    if (headerRows.has(rec.row) || mergeSpans.has(`${rec.row},${rec.col}`)) continue;
    if (isLabelText(rec.text)) {
      rec.candidate = 'label';
      labels.push({ row: rec.row, col: rec.col, text: rec.text });
    }
  }
  for (const { row, col, text } of labels) {
    if (col + 1 <= maxCol && !textGrid[row]![col + 1]) {
      markData(cells, row, col + 1, `标签'${text}'右侧空格`);
    }
    if (row + 1 <= maxRow && !textGrid[row + 1]![col]) {
      markData(cells, row + 1, col, `标签'${text}'下方空格`);
    }
  }

  // 第三遍：无标签数据位定位
  const slotCandidates = detectDataSlots({
    cells,
    textGrid,
    merges,
    dataRegions: dataRegions as Array<{ headerRow: number; headerCol: number; rowCount: number }>,
    headerRows,
    maxRow,
    maxCol,
  });
  for (const slot of slotCandidates) {
    markData(cells, slot.row, slot.col, slot.reason);
  }

  // 区块识别
  const blocks = segmentBlocks({
    maxRow,
    maxCol,
    textGrid,
    merges,
    dataRegions: dataRegions as Array<{ headerRow: number; headerCol: number; rowCount: number }>,
  });

  return {
    sheet: { name: sheetName, rows: maxRow, cols: maxCol },
    rows,
    columns,
    merges,
    cells,
    dataRegions,
    blocks,
  };
}
